import { ImmomioSpinner } from '@immomio/ui/spinner';
import { ImmomioButtonComponent } from '@immomio/ui/button';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  input,
  output,
  viewChild,
  contentChildren,
  computed,
  Signal,
  signal,
  DestroyRef
} from '@angular/core';

import {
  Location,
  NgTemplateOutlet,
  PopStateEvent as LocationEvent,
  ViewportScroller
} from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { FormControlStatus, FormGroup } from '@angular/forms';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subject, SubscriptionLike } from 'rxjs';
import { filter } from 'rxjs/operators';

import { TranslatePipe } from '@ngx-translate/core';

import { ElevationDirective } from '../../../directives/elevation/elevation.directive';
import { BodyService } from '../../../infrastructure/browser/body/body.service';
import { WizardStepDirective } from './wizard-step/wizard-step.directive';
import { Step } from './models/step.model';
import { StepChange } from './models/step-change.model';

import { WizardProgressComponent } from './wizard-progress/wizard-progress.component';

@Component({
  selector: 'app-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImmomioSpinner,
    ImmomioButtonComponent,
    WizardProgressComponent,
    NgTemplateOutlet,
    TranslatePipe,
    ElevationDirective
  ]
})
export class WizardComponent implements OnDestroy, OnInit, AfterViewInit {
  #destroyRef = inject(DestroyRef);
  private location = inject(Location);
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);
  private bodyService = inject(BodyService);

  readonly currentForm = input<FormGroup>(undefined);
  readonly steps = input<Step[]>(undefined);
  readonly currentStepNumber = input<number>(undefined);
  readonly currentStepValid = input<boolean>(undefined);
  readonly allowCancel = input(false);
  readonly allowCancelInAnyStep = input(false);
  readonly allowBack = input(true);
  readonly isProcessing = input(false);
  readonly hideButtonContainer = input(false);
  readonly wrapStepContainer = input(false);
  readonly hideNextButton = input(false);
  readonly floatingTop = input(true);
  readonly floatingActions = input(true);
  readonly formElement = input<HTMLFormElement>(undefined);
  readonly alwaysEnableButton = input(false);
  readonly failedContinue = output();
  readonly wizardScrollAnchor = viewChild<ElementRef>('wizardScrollAnchor');
  readonly formValid = computed(() => {
    const currentForm = this.currentForm();
    const statusReactive = currentForm?.[
      'statusReactive'
    ] as Signal<FormControlStatus>;

    return statusReactive?.() === 'VALID';
  });

  readonly viewLoaded = signal(false);

  readonly stepTemplates = contentChildren(WizardStepDirective, {
    read: TemplateRef
  });

  public stepChange: Subject<StepChange>;
  private locationSubscription: SubscriptionLike;

  ngOnInit() {
    this.stepChange = new Subject<StepChange>();

    this.locationSubscription = this.location.subscribe(location =>
      this.handleLocationChange(location)
    );

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.viewportScroller.scrollToPosition([0, 0]));

    this.bodyService.setBodyModifierClass('wizard-actions-visible');
  }

  ngAfterViewInit() {
    // We wait for the view to be fully loaded before showing the wizard__actions-container,
    // otherwise edit.property.component will always be scrolled to bottom on init
    this.viewLoaded.set(true);
  }

  ngOnDestroy() {
    this.stepChange?.complete();
    this.locationSubscription?.unsubscribe();
    this.bodyService.unsetBodyModifierClass('wizard-actions-visible');
  }

  next() {
    const isValid = this.formValid() || this.currentStepValid();

    if (!isValid) {
      this.formElement()?.dispatchEvent(
        new Event('submit', { cancelable: true })
      );
      this.currentForm().markAllAsTouched();
      this.failedContinue.emit();
    } else {
      this.stepChange.next({ action: 'next' });
      // TODO: check if this causes the invalid jump error
      this.viewportScroller.scrollToPosition([0, 0]);
      (
        this.wizardScrollAnchor().nativeElement as HTMLDivElement
      ).scrollIntoView();
    }
  }

  back() {
    if (!this.allowBack()) return;
    this.stepChange.next({ action: 'back' });
    (
      this.wizardScrollAnchor().nativeElement as HTMLDivElement
    ).scrollIntoView();
  }

  select(step: Step) {
    if (!step.selectable) return;
    this.stepChange.next({ action: 'select', step });
    (
      this.wizardScrollAnchor().nativeElement as HTMLDivElement
    ).scrollIntoView();
  }

  complete() {
    const isValid = this.formValid() || this.currentStepValid();

    if (!isValid) {
      this.formElement().dispatchEvent(
        new Event('submit', { cancelable: true })
      );
      this.currentForm().markAllAsTouched();
      this.failedContinue.emit();
    } else {
      this.stepChange.next({ action: 'complete' });
      (
        this.wizardScrollAnchor().nativeElement as HTMLDivElement
      ).scrollIntoView();
    }
  }

  cancel() {
    if (!this.allowCancel()) return;
    this.stepChange.next({ action: 'cancel' });
    (
      this.wizardScrollAnchor().nativeElement as HTMLDivElement
    ).scrollIntoView();
  }

  private handleLocationChange(location: LocationEvent) {
    const stepName = location.url.split('/').pop();
    const isBeforeCurrent =
      this.steps().findIndex(step => step.name === stepName) <
      this.currentStepNumber() - 1;
    const isAfterCurrent =
      this.steps()[this.currentStepNumber()] &&
      this.steps()[this.currentStepNumber()].name === stepName;
    this.viewportScroller.scrollToPosition([0, 0]);

    if (isBeforeCurrent) {
      this.back();
    }

    if (isAfterCurrent) {
      this.next();
    }
  }
}
